import { RefObject, useEffect, useMemo, useState } from 'react';
import { DateRange, Range, RangeKeyDict } from 'react-date-range';
import { createPortal } from 'react-dom';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import {
  MdCheck,
  MdClose,
  MdOutlineSettingsBackupRestore,
} from 'react-icons/md';
import { usePopperTooltip } from 'react-popper-tooltip';

import { Placement } from '@popperjs/core';
import { KEYBOARD, ONE_SECOND_MILLISECONDS } from 'commons';
import { Button, Text } from 'components';
import { addDays, endOfToday, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useScreenInfo } from 'hooks';

import { DateRangeInput } from './components';
import * as S from './styles';

type Options = {
  placement?: Placement;
  scrollIntoView?: boolean;
};

interface DateRangePickerProps {
  values?: Range;
  isFuturePeriod?: boolean;
  mainRef?: RefObject<HTMLDivElement>;
  options?: Options;
  minimumDate?: Date;
  maximumDate?: Date;
  maxPeriod?: number;
  onSubmit?: () => Promise<any>;
  onReset: VoidFunction;
  onChangeRange: (dates: RangeKeyDict) => void;
}

const MILLISECONDS_DAY = 24 * 60 * 60 * ONE_SECOND_MILLISECONDS;

const MIN_PERIOD = 1;
const MAX_PERIOD = 90;

export function DateRangePicker({
  values,
  isFuturePeriod,
  mainRef,
  options,
  minimumDate,
  maximumDate,
  maxPeriod = MAX_PERIOD,
  onSubmit,
  onReset,
  onChangeRange,
}: DateRangePickerProps) {
  const minDate = useMemo(
    () => (isFuturePeriod ? startOfDay(addDays(new Date(), 1)) : minimumDate),
    [isFuturePeriod, minimumDate]
  );
  const maxDate = useMemo(
    () => (isFuturePeriod === false ? endOfToday() : maximumDate),
    [isFuturePeriod, maximumDate]
  );

  const { isMobile, isSmallDesktop, isTablet, windowSize } = useScreenInfo();
  const isSmallScreen = isSmallDesktop || isTablet || isMobile;

  const [controlledVisible, setControlledVisible] = useState(false);

  const [ranges, setRanges] = useState<Range[]>([
    {
      ...values!,
      key: 'selection',
      showDateDisplay: true,
      autoFocus: true,
    },
  ]);

  const startDateTimestamp = values?.startDate?.getTime() || 0;
  const endDateTimestamp = values?.endDate?.getTime() || 0;
  const totalDaysMilliseconds = Math.abs(startDateTimestamp - endDateTimestamp);
  const totalDays =
    Math.ceil(totalDaysMilliseconds / MILLISECONDS_DAY) || MIN_PERIOD;
  const exceedTheMaxPeriod = maxPeriod ? totalDays > maxPeriod : false;

  const { getTooltipProps, setTooltipRef, setTriggerRef, visible, tooltipRef } =
    usePopperTooltip(
      {
        trigger: 'click',
        delayShow: 200,
        visible: controlledVisible,
        onVisibleChange: setControlledVisible,
        closeOnOutsideClick: true,
      },
      {
        placement: options?.placement || 'bottom-start',
      }
    );

  function onApply() {
    onSubmit?.();
    setControlledVisible(false);
  }

  function handleToggleDatePicker() {
    setControlledVisible(prevState => !prevState);
  }

  useEffect(() => {
    const handleKeyDown = ({ key }: KeyboardEvent) => {
      if (key === KEYBOARD.esc) {
        setControlledVisible(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setRanges(prevState => [{ ...prevState, ...values }]);
  }, [values]);

  useEffect(() => {
    if (!tooltipRef || !options?.scrollIntoView) return;

    const tooltipRect = tooltipRef.getBoundingClientRect();
    const windowHeight = windowSize.height;

    const isTooltipVisible = tooltipRect.bottom < windowHeight;

    if (!isTooltipVisible) {
      mainRef?.current?.scrollTo({
        top: tooltipRect.bottom - windowHeight + 60,
        behavior: 'smooth',
      });
    }
  }, [mainRef, options?.scrollIntoView, tooltipRef, windowSize.height]);

  const renderPeriodText = (() => {
    const period = isFuturePeriod ? 'FUTURO' : 'RETROATIVO';
    const text = isFuturePeriod ? 'após o dia de hoje' : 'passadas';
    return (
      <Text>
        Ao analisar o período <strong>{period}</strong> você só pode escolher
        datas {text}
      </Text>
    );
  })();

  const renderTotalDays = (() => {
    if (startDateTimestamp && endDateTimestamp) {
      const totalDaysText = totalDays === 1 ? 'dia' : 'dias';
      const exceededPeriodText = exceedTheMaxPeriod
        ? `(máximo ${maxPeriod} dias)`
        : '';

      return (
        <S.SelectedPeriod $danger={exceedTheMaxPeriod}>
          <Text>
            Período selecionado: <b>{totalDays}</b> {totalDaysText}{' '}
            {exceededPeriodText}
          </Text>
        </S.SelectedPeriod>
      );
    }

    return null;
  })();

  function renderDatePicker() {
    const datePickerRender = (() => (
      <S.DateRangePickerWrapper
        ref={setTooltipRef}
        {...getTooltipProps({ className: 'date-picker-container' })}
      >
        {isMobile && (
          <S.CloseButton onClick={() => setControlledVisible(false)}>
            <MdClose size={20} />
          </S.CloseButton>
        )}
        {isFuturePeriod !== undefined && (
          <S.DateRangeWarning>
            <S.IconWrapper>
              <AiOutlineInfoCircle size={24} />
            </S.IconWrapper>
            {renderPeriodText}
          </S.DateRangeWarning>
        )}

        <S.DateRangePickerContent>
          <DateRange
            locale={ptBR}
            onChange={onChangeRange}
            moveRangeOnFirstSelection={false}
            months={2}
            minDate={minDate}
            maxDate={maxDate}
            ranges={ranges}
            direction={isSmallScreen ? 'vertical' : 'horizontal'}
            dateDisplayFormat="dd/MM/yyyy"
            showDateDisplay={false}
            weekdayDisplayFormat="EEEEE"
          />
        </S.DateRangePickerContent>

        {renderTotalDays}

        <S.DateRangeActions>
          <S.ResetButton onClick={onReset} type="button">
            <MdOutlineSettingsBackupRestore size={24} />
            Retornar ao padrão
          </S.ResetButton>
          <Button onClick={onApply} type="button" disabled={exceedTheMaxPeriod}>
            <MdCheck size={24} />
            Aplicar
          </Button>
        </S.DateRangeActions>
      </S.DateRangePickerWrapper>
    ))();

    const shouldUsePortal = !options?.scrollIntoView || isMobile;

    return shouldUsePortal
      ? createPortal(datePickerRender, document.body)
      : datePickerRender;
  }

  return (
    <S.Wrapper>
      <S.Overlay
        $isOpen={visible}
        onClick={() => setControlledVisible(false)}
      ></S.Overlay>
      <DateRangeInput
        ref={setTriggerRef}
        isDatePickerOpen={controlledVisible}
        dateRange={values!}
        callback={handleToggleDatePicker}
      />

      {visible && renderDatePicker()}
    </S.Wrapper>
  );
}
